import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  arrayOf as arrayType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  extend,
  useDataModel
} from '@thd-nucleus/data-sources';
import classNames from 'classnames/bind';
import { useLocation, useNavigate } from '@thd-olt-component-react/router';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Metadata } from '@thd-olt-component-react/metadata';
import { Pagination } from '@thd-olt-component-react/pagination';
import { ProductCategoryVisnav } from '@thd-olt-component-react/product-category-visnav';
import { RoomVoInspire } from '@thd-olt-component-react/augmented-reality';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import {
  Row,
  Col,
  DropDown
} from '@thd-olt-component-react/core-ui';
import {
  Button,
  Checkbox,
  FormGroup,
  SelectionControlLabel,
  Chip,
  Card,
  CardActions,
  CardMedia,
  Skeleton, SkeletonLine, SkeletonBlock,
  Link,
  Typography,
  CardTitle, CardBody, CardContent
} from '@one-thd/sui-atomic-components';
import { PaginationLoader } from './PaginationLoader';
import {
  constructURL,
  clearAndUpdateURL,
  usePathnameParams,
  capitalizeFirstLetter
} from './roomUtils';
import {
  NUMBER_OF_RESULTS,
  ROOM_KEY,
  STYLE_KEY,
  COLOR_KEY,
  PAGE_KEY,
  MOBILE_CHANNEL,
  FIRST_PAGE
} from './Constants';
import styles from './room.module.scss';

export const Room = () => {
  declareContexts([ExperienceContext]);
  const urlParams = new URLSearchParams(useLocation().search);
  const breadCrumbsLabels = [{ label: 'Shop By Room', url: '/room' }];
  const { channel } = useContext(ExperienceContext);
  const navigate = useNavigate();
  const location = useLocation();
  const cx = classNames.bind(styles);
  const roomUrlParams = urlParams.get(ROOM_KEY)?.split('|');
  const styleUrlParams = urlParams.get(STYLE_KEY)?.split('|');
  const colorUrlParams = urlParams.get(COLOR_KEY)?.split('|');
  const page = urlParams.get(PAGE_KEY) ? urlParams.get(PAGE_KEY) : 1;
  const { roomParam, styleParam, colorParam } = usePathnameParams();
  const topOfPage = useRef(null);

  let roomOptions = [];
  let styleOptions = [];
  let colorOptions = [];

  if (roomParam) {
    roomOptions.push(roomParam);
  }
  if (styleParam) {
    styleOptions.push(styleParam);
  }
  if (colorParam) {
    colorOptions.push(colorParam);
  }
  if (roomUrlParams) {
    roomOptions.push(...roomUrlParams);
  }
  if (styleUrlParams) {
    styleOptions.push(...styleUrlParams);
  }
  if (colorUrlParams) {
    colorOptions.push(...colorUrlParams);
  }
  const [selectedFilters, setSelectedFilter] = useState({
    roomOptions,
    styleOptions,
    colorOptions
  });
  const [showHeroBanner, setShowHeroBanner] = useState(roomOptions.length <= 1);
  const [currentUrl, setCurrentUrl] = useState(location.pathname);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handlePopState = () => {
        setCurrentUrl(location.pathname);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
    return undefined; // Explicitly return undefined to satisfy ESLint
  }, []);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('sbr.page-load');
  }, []);

  const variables = {
    color: colorOptions.toString(),
    style: styleOptions.toString(),
    room: roomOptions.toString(),
    pageSize: NUMBER_OF_RESULTS.toString(),
    startIndex: ((page - 1) * NUMBER_OF_RESULTS).toString()
  };

  const { data, loading, error } = useDataModel('collectionList', { variables, ssr: false });

  if (error) return null;

  const {
    dimensions = [],
    searchReport = {},
    collections = [],
    categories = {},
    metadata = {},
    breadCrumbs = []
  } = data?.collectionList || {};
  const {
    H1Tag = '',
    title = '',
    baseUrl = '',
    canonicalUrl = '',
    metaDescription = '',
    imageUrl: metadataImageUrl = ''
  } = metadata;
  const {
    collectionUrl = '',
    imageUrl: heroImageUrl = '',
    room: heroRoom = '',
    items = []
  } = categories || {};
  const { collectionCount = 0 } = searchReport;
  const totalPages = Math.ceil(collectionCount / NUMBER_OF_RESULTS);
  const pageStart = (page - 1) * NUMBER_OF_RESULTS + 1;
  const pageEnd = NUMBER_OF_RESULTS * page < collectionCount ? NUMBER_OF_RESULTS * page : collectionCount;
  const visNavList = {
    searchModel: {
      relatedResults: {
        visualNavs: items?.map((item) => ({
          label: item?.name,
          webUrl: item?.href,
          imageId: 0,
          imageURL: item?.imageUrl
        }))
      }
    }
  };

  const metadataTitle = title ? `${title} - The Home Depot` : 'The Home Depot';

  const metaInfo = {
    title: metadataTitle,
    description: metaDescription,
    ogTitle: metadataTitle,
    ogImage: metadataImageUrl,
    ogType: 'product',
    siteName: 'The Home Depot',
    facebookAdmins: '100005416960152',
    canonicalURL: baseUrl,
    imageUrl: metadataImageUrl
  };

  const scrollToTop = () => topOfPage.current.scrollIntoView({ behavior: 'smooth' });

  const clearAllSelectedFilters = () => {
    setShowHeroBanner(true);
    setSelectedFilter({ styleOptions: [], colorOptions: [], roomOptions: [] });
    const url = constructURL([], [], [], FIRST_PAGE);
    navigate(url);
  };

  const clearSelectedFilter = (selection) => {
    const styleFilters = selectedFilters.styleOptions || [];
    const colorFilters = selectedFilters.colorOptions || [];
    const roomFilters = selectedFilters.roomOptions || [];
    if (styleFilters.includes(selection)) {
      const index = styleFilters.indexOf(selection);
      if (index > -1) {
        styleFilters.splice(index, 1);
      } else {
        styleFilters.push(selection);
      }
    } else if (colorFilters.includes(selection)) {
      const index = colorFilters.indexOf(selection);
      if (index > -1) {
        colorFilters.splice(index, 1);
      } else {
        colorFilters.push(selection);
      }
    } else if (roomFilters.includes(selection)) {
      const index = roomFilters.indexOf(selection);
      if (index > -1) {
        roomFilters.splice(index, 1);
      } else {
        roomFilters.push(selection);
      }
    }

    setShowHeroBanner(roomFilters.length <= 1);

    navigate(constructURL(styleFilters, colorFilters, roomFilters, FIRST_PAGE));
  };

  const updatePage = (pageNumber) => {
    if (pageNumber > 0) {
      const styleFilters = selectedFilters.styleOptions || [];
      const colorFilters = selectedFilters.colorOptions || [];
      const roomFilters = selectedFilters.roomOptions || [];
      navigate(constructURL(styleFilters, colorFilters, roomFilters, pageNumber));
      scrollToTop();
    }
  };

  const onApplyClicked = () => {
    const styleFilters = selectedFilters.styleOptions || [];
    const colorFilters = selectedFilters.colorOptions || [];
    const roomFilters = selectedFilters.roomOptions || [];
    setShowHeroBanner(roomFilters.length <= 1);
    navigate(constructURL(styleFilters, colorFilters, roomFilters, FIRST_PAGE));
  };

  const handleOptionsClick = (refinementKey, dimensionLabel) => {
    const styleFilters = selectedFilters.styleOptions || [];
    const colorFilters = selectedFilters.colorOptions || [];
    const roomFilters = selectedFilters.roomOptions || [];
    if (dimensionLabel === STYLE_KEY) {
      const index = styleFilters ? styleFilters.indexOf(refinementKey) : -1;
      if (index > -1) {
        styleFilters.splice(index, 1);
      } else {
        styleFilters.push(refinementKey);
        styleFilters.sort();
      }
    } else if (dimensionLabel === COLOR_KEY) {
      const index = colorFilters ? colorFilters.indexOf(refinementKey) : -1;
      if (index > -1) {
        colorFilters.splice(index, 1);
      } else {
        colorFilters.push(refinementKey);
        colorFilters.sort();
      }
    } else if (dimensionLabel === ROOM_KEY) {
      const index = roomFilters ? roomFilters.indexOf(refinementKey) : -1;
      if (index > -1) {
        roomFilters.splice(index, 1);
      } else {
        roomFilters.push(refinementKey);
        roomFilters.sort();
      }
    }

    setSelectedFilter({
      styleOptions: styleFilters,
      colorOptions: colorFilters,
      roomOptions: roomFilters
    });
  };

  const cardFilterSelection = (filter) => {
    const link = clearAndUpdateURL(filter);
    navigate({
      pathname: `/room/${link}`
    });
  };

  const isChecked = (dimensionLabel, refinementKey) => {
    const styleFilters = selectedFilters.styleOptions || [];
    const colorFilters = selectedFilters.colorOptions || [];
    const roomFilters = selectedFilters.roomOptions || [];
    const selectedDimensions = [...styleFilters, ...colorFilters, ...roomFilters];
    return selectedDimensions?.some(
      (element) => element.toLowerCase() === refinementKey.toLowerCase()
    );
  };

  const routeToCollection = (newUrl) => {
    navigate(newUrl);
    setCurrentUrl(newUrl);
  };

  return (
    <div className="grid isBound" ref={topOfPage}>
      <ErrorBoundary name="sbr-metadata">
        <Metadata data={metaInfo} />
      </ErrorBoundary>
      <Col className={styles['room-results-container']}>
        <ErrorBoundary name="sbr-content">
          <Row style={{ maxHeight: '22px' }}>
            <ErrorBoundary name="sbr-breadcrumbs">
              <Breadcrumbs staticCrumbs={breadCrumbsLabels} hideOnNoCategory={false} />
            </ErrorBoundary>
          </Row>
          {loading ? (
            <div className="sui-py-2">
              <Skeleton disablePadding grow>
                <SkeletonBlock height={60} />
              </Skeleton>
            </div>
          ) : (
            heroImageUrl && showHeroBanner && (
              <Row className={styles['hero-container']}>
                <ErrorBoundary name="sbr-hero-banner">
                  <img
                    className={styles['hero-banner-background']}
                    src={heroImageUrl}
                    alt="Non Lazy"
                    title={heroRoom}
                    width={1440}
                    height={250}
                  />
                  <div className={styles['hero-banner-content']}>
                    <h1 className={styles['hero-banner-content-string']}>{H1Tag}</h1>
                  </div>
                </ErrorBoundary>
              </Row>
            )
          )}

          {!loading && (
            <Row className="sui-py-6">
              <ErrorBoundary name="sbr-roomvo-inspire-banner">
                <RoomVoInspire />
              </ErrorBoundary>
            </Row>
          )}

          {!loading && categories?.items?.length > 0 && (
            <ErrorBoundary name="product-category-visnav-container">
              <ProductCategoryVisnav data={visNavList} />
            </ErrorBoundary>
          )}

          {loading ? (
            <div className="sui-py-2">
              <Skeleton disablePadding grow>
                <SkeletonBlock height={14} />
              </Skeleton>
            </div>
          ) : (
            <Row>
              <ErrorBoundary name="sbr-dropdowns">
                <Col fallback="0" md="1" lg="2" nopadding />
                <Col fallback="12" md="10" lg="8" nopadding={channel !== MOBILE_CHANNEL}>
                  {dimensions.map((dim, dimIndex) => {
                    const { refinements = [], label: dimensionLabel = '' } = dim;
                    const dropdownWrapperClasses = cx('dropdown-wrapper', [`dropdown-${dimIndex}`]);
                    return (
                      <Col fallback="4" nopadding={channel === MOBILE_CHANNEL}>
                        <ErrorBoundary name="sbr-dimensions">
                          <DropDown
                            header={capitalizeFirstLetter(dimensionLabel)}
                            expandOnClick={channel === MOBILE_CHANNEL}
                            contentClass={dropdownWrapperClasses}
                          >
                            <div className={styles['scrollable-dropdown']}>
                              {refinements.map((refinement) => {
                                const {
                                  refinementKey = '',
                                  label: refinementLabel = '',
                                  url = ''
                                } = refinement;
                                return (
                                  <div>
                                    <FormGroup gap={false}>
                                      <SelectionControlLabel
                                        value={refinementKey}
                                        id={refinementKey}
                                        label={refinementLabel}
                                      >
                                        <Checkbox
                                          onChange={() => handleOptionsClick(refinementKey, dimensionLabel)}
                                          checked={isChecked(dimensionLabel, refinementKey)}
                                        />
                                      </SelectionControlLabel>
                                    </FormGroup>
                                  </div>
                                );
                              })}
                            </div>
                            <Col>
                              <Button onClick={onApplyClicked} variant="secondary" fullWidth>
                                Apply
                              </Button>
                            </Col>
                          </DropDown>
                        </ErrorBoundary>
                      </Col>
                    );
                  })}
                </Col>
                <Col fallback="0" md="1" lg="2" nopadding />
              </ErrorBoundary>
            </Row>
          )}

          {!loading && breadCrumbs?.length > 0 && (
            <Row className={styles['filter-container']}>
              <ErrorBoundary name="sbr-filter-pills">
                <ul className="sui-flex sui-justify-center sui-flex-wrap sui-p-1 sui-list-none">
                  {breadCrumbs.map((breadCrumb) => (
                    <li className="sui-m-2" key={breadCrumb?.refinementKey}>
                      <Chip
                        label={breadCrumb?.label}
                        onClick={() => clearSelectedFilter(breadCrumb?.refinementKey)}
                      />
                    </li>
                  ))}
                </ul>
                <Button
                  variant="text"
                  onClick={clearAllSelectedFilters}
                  link
                  className={styles['filter-clear-all']}
                >
                  Clear All
                </Button>
              </ErrorBoundary>
            </Row>
          )}
          <ErrorBoundary name="sbr-pod-container">
            <div
              className="sui-grid sui-grid-cols-1 sm:sui-grid-cols-2 md:sui-grid-cols-3 xl:sui-grid-cols-4 sui-gap-4"
            >
              {loading
                ? [...Array(24)].map((loader, index) => (
                  <Skeleton>
                    <SkeletonBlock aspect="wide" />
                    <SkeletonLine variant="multi" numberOfLines={5} />
                  </Skeleton>
                ))
                : (
                  <>
                    {collections?.map((collection) => {
                      const { metadata: collectionMetadata = {}, url = '' } = collection;
                      const { imageUrl = '', name = '', style = [], room = [] } = collectionMetadata;
                      let selectables = {};
                      if (style) {
                        style.forEach((element) => {
                          selectables[element] = STYLE_KEY;
                        });
                      }

                      if (room) {
                        room.forEach((element) => {
                          selectables[element] = ROOM_KEY;
                        });
                      }

                      const lastElement = Object.keys(selectables).length - 1;

                      return (
                        <Card>
                          <CardMedia
                            src={imageUrl}
                            alt="Stretchy Image"
                            aspect="wide"
                            onClick={() => routeToCollection(url)}
                          />
                          <CardTitle
                            header={(
                              /*eslint-disable */
                              <Link component="button" onClick={() => routeToCollection(url)} underline="hover">
                                <Typography component="h4" variant="h4" weight="bold">{name}</Typography>
                              </Link>
                            )}
                          />
                          <CardContent grow disablePadding disableGutters>
                            <CardBody />
                            <CardActions>
                              <div className={styles['collection-pod__details__filters']} style={{ marginTop: '0px' }}>
                                {Object.entries(selectables).map((filter, index) => {
                                  const filterUrl = ''; // TODO:  construct URL for SEO?
                                  return (
                                    <span key={filter}>
                                      <Link
                                        onClick={() => cardFilterSelection(filter)}
                                        href={filterUrl}
                                        color="subtle"
                                        underline="always"
                                      >
                                        {filter[0]}
                                      </Link>
                                      {index !== lastElement && (
                                        <span className={styles['filter-spacer']}>|</span>
                                      )}
                                    </span>
                                  );
                                })}
                              </div>
                            </CardActions>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </>
                )}
            </div>
          </ErrorBoundary>

          <Row>
            <ErrorBoundary name="sbr-pager">
              <Col className={styles['collection-pager']}>
                {loading && <PaginationLoader />}
                {!loading && collectionCount > NUMBER_OF_RESULTS && (
                  <>
                    <Pagination
                      currentPage={parseInt(page, 10)}
                      maxNumericBlocks={5}
                      onPageChanged={updatePage}
                      showArrows
                      totalPages={totalPages}
                    />
                    <span className="collection-pager__subtext">
                      Showing{' '}
                      <b>
                        {pageStart} - {pageEnd}
                      </b>{' '}
                      of <b>{collectionCount}</b> results.
                    </span>
                  </>
                )}
              </Col>
            </ErrorBoundary>
          </Row>
        </ErrorBoundary>
      </Col>
    </div>
  );
};

Room.dataModel = extend(
  {
    collectionList: params({
      style: stringType(),
      color: stringType(),
      room: stringType(),
      pageSize: stringType(),
      startIndex: stringType()
    }).shape({
      breadCrumbs: arrayType(
        shapeType({
          label: stringType(),
          dimensionName: stringType(),
          deselectUrl: stringType(),
          refinementKey: stringType()
        })
      ),
      dimensions: arrayType(
        shapeType({
          label: stringType(),
          refinements: arrayType(
            shapeType({
              label: stringType(),
              url: stringType(),
              refinementKey: stringType()
            })
          )
        })
      ),
      collections: arrayType(
        shapeType({
          url: stringType(),
          metadata: shapeType({
            name: stringType(),
            style: arrayType(stringType()),
            room: arrayType(stringType()),
            imageUrl: stringType()
          })
        })
      ),
      metadata: shapeType({
        canonicalUrl: stringType(),
        baseUrl: stringType(),
        collectionCount: numberType(),
        H1Tag: stringType(),
        metaDescription: stringType(),
        title: stringType()
      }),
      searchReport: shapeType({
        collectionCount: numberType()
      }),
      categories: shapeType({
        collectionId: stringType(),
        room: stringType(),
        title: stringType(),
        imageUrl: stringType(),
        collectionUrl: stringType(),
        items: arrayType(
          shapeType({
            name: stringType(),
            href: stringType(),
            imageUrl: stringType()
          })
        )
      })
    })
  },
  Breadcrumbs
);
